import { useAuth, useSnack } from 'hooks';
import { AppContext } from '../contexts';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useAsync } from 'react-async';
import { getToConnectionRequests } from 'api/node/toConnectionRequest';
import { getLocales } from 'api/node/locales';
import { me } from 'api/node/users';
import { RoleEnum } from '@koob/enums';
import { actingOrganizationOf } from '@koob/utils';

const permissions = {
  inviteUser: [RoleEnum.KOOB_ADMIN, RoleEnum.DMC_ADMIN],
  manageOrganizations: [RoleEnum.KOOB_ADMIN, RoleEnum.DMC_ADMIN],
  readSelfOrganization: [RoleEnum.DMC_USER],
  manageSelfOrganization: [RoleEnum.DMC_ADMIN],
  manageSelfOrganizationUsers: [RoleEnum.KOOB_ADMIN, RoleEnum.DMC_ADMIN],
  discardOrganizations: [RoleEnum.KOOB_ADMIN],
  readUsers: [RoleEnum.KOOB_ADMIN],
  manageUsers: [RoleEnum.KOOB_ADMIN],
  readLocations: [RoleEnum.KOOB_ADMIN, RoleEnum.DMC_ADMIN, RoleEnum.DMC_USER],
  manageLocations: [RoleEnum.KOOB_ADMIN],
  manageExperienceSettings: [RoleEnum.KOOB_ADMIN],
  manageHotelSettings: [RoleEnum.KOOB_ADMIN],
  manageTravelDiarySettings: [RoleEnum.KOOB_ADMIN],
  readExperienceSettings: [
    RoleEnum.KOOB_ADMIN,
    RoleEnum.DMC_ADMIN,
    RoleEnum.DMC_USER,
  ],
  readHotelSettings: [
    RoleEnum.KOOB_ADMIN,
    RoleEnum.DMC_ADMIN,
    RoleEnum.DMC_USER,
  ],
  readOrganizationSettings: [RoleEnum.KOOB_ADMIN],
  manageIntegrationsSettings: [RoleEnum.KOOB_ADMIN],
  readAuditLog: [RoleEnum.KOOB_ADMIN, RoleEnum.DMC_ADMIN],
  readTravelerSettings: [
    RoleEnum.KOOB_ADMIN,
    RoleEnum.DMC_ADMIN,
    RoleEnum.DMC_USER,
  ],
  readTravelDiarySettings: [
    RoleEnum.KOOB_ADMIN,
    RoleEnum.DMC_ADMIN,
    RoleEnum.DMC_USER,
  ],
  readTravelDiaryContent: [RoleEnum.KOOB_ADMIN, RoleEnum.DMC_ADMIN],
  manageTravelDiaryContent: [RoleEnum.KOOB_ADMIN, RoleEnum.DMC_ADMIN],
  manageTravelerSettings: [RoleEnum.KOOB_ADMIN],
  manageErrorDictionary: [RoleEnum.KOOB_ADMIN],
  createHotel: [RoleEnum.DMC_ADMIN, RoleEnum.DMC_USER],
  readHotel: [RoleEnum.DMC_ADMIN, RoleEnum.DMC_USER],
  readBookings: [RoleEnum.KOOB_ADMIN, RoleEnum.DMC_ADMIN, RoleEnum.DMC_USER],
  manageBookings: [RoleEnum.KOOB_ADMIN, RoleEnum.DMC_ADMIN, RoleEnum.DMC_USER],
  deleteBookings: [RoleEnum.DMC_ADMIN],
  manageCities: [RoleEnum.KOOB_ADMIN],
  canManageToRequestAccess: [RoleEnum.KOOB_ADMIN],
  canManageToConnectionRequest: [RoleEnum.KOOB_ADMIN, RoleEnum.DMC_ADMIN],
  manageKoobAdmin: [RoleEnum.KOOB_ADMIN],
  canShowcaseHotels: [RoleEnum.KOOB_ADMIN, RoleEnum.DMC_ADMIN],
  impersonate: [RoleEnum.KOOB_ADMIN],
};

const AppProvider = ({ children }) => {
  const { accessToken, logout } = useAuth();
  const { t } = useTranslation();
  const { notify } = useSnack();

  const { data: toConnectionRequestData } = useAsync({
    promiseFn: getToConnectionRequests,
  });

  const allowedConnectionRequests = toConnectionRequestData?.data;

  const { data: user, error } = useAsync({
    promiseFn: me,
    skip: !accessToken,
  });

  const currentUser = user?.data;
  const [
    selectedDistributionOrganization,
    setSelectedDistributionOrganization,
  ] = useState(actingOrganizationOf(currentUser));

  useEffect(() => {
    if (error) {
      logout();
      notify(t('errors:userNotAuthorized'), { type: 'error' });
    }
  }, [error]);

  const currentUserRole = currentUser?.role;
  // The organization this tab acts as (KD-3735 picker), else the first.
  const currentOrganization = actingOrganizationOf(currentUser);
  const currentOrganizationPosition = {
    lat: parseFloat(currentOrganization?.lat),
    lon: parseFloat(currentOrganization?.lon),
  };

  if (currentUserRole && currentUserRole.startsWith('to_')) {
    logout();
    notify(t('errors:userNotAuthorized'), { type: 'error' });
  }

  const { data: localesData } = useAsync({
    promiseFn: getLocales,
  });

  const locales = localesData?.data?.map(({ name, description }) => ({
    value: name,
    label: name.toUpperCase(),
    alpha2: description.toLowerCase(),
  }));

  // const handleUpdateQueryLocale = locale => {
  //   localStorage.setItem('queryLocale', locale);
  //   setQueryLocale(locale);
  // };

  const canManageUser = (targetUser) => {
    const targetUserRole = targetUser?.role || RoleEnum.DMC_USER;

    if (currentUserRole === RoleEnum.KOOB_ADMIN) return true;

    if (targetUser?.id === currentUser?.id) {
      return false;
    }

    switch (currentUserRole) {
      case RoleEnum.SUPER_ADMIN:
        switch (targetUserRole) {
          case RoleEnum.KOOB_ADMIN:
            return true;
          default:
            return false;
        }
      case RoleEnum.DMC_ADMIN:
        switch (targetUserRole) {
          case RoleEnum.DMC_USER:
            return true;
          default:
            return false;
        }
      case RoleEnum.DMC_USER:
      default:
        return false;
    }
  };

  const isAllowedTo = (name) =>
    (permissions[name] || []).indexOf(currentUserRole) !== -1;

  // useDeepCompareEffect(() => {
  //   client.resetStore();
  // }, [{ queryLocale }]);

  if (!Boolean(currentUser)) {
    return null;
  }

  const canManageToRequestAccess = () => {
    if (currentUserRole === RoleEnum.KOOB_ADMIN) {
      return true;
    }
  };

  // KD-3735: an organization reached through a group (a child of a group the
  // user belongs to) is read-only for them; only KOOB admins and the
  // organization's own members edit it (the API refuses the rest).
  const groupIds = new Set(
    (currentUser?.organizations ?? [])
      .filter(({ isGroup }) => isGroup)
      .map(({ organizationId }) => String(organizationId)),
  );
  const canEditOrganization = (organizationId) =>
    currentUserRole === RoleEnum.KOOB_ADMIN ||
    (currentUser?.organizations ?? []).some(
      (organization) =>
        String(organization.organizationId) === String(organizationId) &&
        !groupIds.has(String(organization.parentId)),
    );

  const canManageToConnectionRequest = (targetUser) => {
    if (currentUserRole === RoleEnum.KOOB_ADMIN) {
      return true;
    }
    if (currentUserRole === RoleEnum.DMC_ADMIN && targetUser.toRequest) {
      return true;
    }
    if (
      currentUserRole === RoleEnum.DMC_USER &&
      allowedConnectionRequests?.length >= 1
    ) {
      return true;
    }
  };

  return (
    <AppContext.Provider
      value={{
        user: currentUser,
        currentUserRole,
        currentOrganizationId: currentOrganization?.id,
        currentOrganization,
        currentOrganizationIdInt: currentOrganization?.organizationId,
        canManageUser,
        canManageToRequestAccess,
        canManageToConnectionRequest,
        canEditOrganization,
        isAllowedTo,
        locales,
        selectedDistributionOrganization,
        setSelectedDistributionOrganization,
        currentOrganizationPosition,
        // queryLocale,
        // updateQueryLocale: handleUpdateQueryLocale,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export default AppProvider;
